// src/penpal.ts
// Shared helpers for pen pal requests, pen pal links, kudos, blocking,
// and reports, so every button saves the same shape of data.
//
// Safety model (enforced by firestore.rules too):
// - Strangers can only send a pen pal request (with an optional note) or kudos.
// - Accepting a request creates a link in `penpals/{pairId}`; letters can only
//   be sent between linked accounts.
// - Blocking someone removes the link and stops their requests, kudos, and letters.
import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebase";
import { emailReport } from "./reportEmail";
import { PaceKind, checkPace, isSlowDown, markPace } from "./pace";

// Adds a new document together with its speed-limit stamp (see pace.ts).
// Throws SlowDown if it's too soon; the friendly note is already showing.
export async function pacedAdd(uid: string, kind: PaceKind, col: string, data: Record<string, unknown>) {
  await checkPace(uid, kind);
  const ref = doc(collection(db, col));
  const batch = writeBatch(db);
  markPace(batch, uid, kind);
  batch.set(ref, data);
  await batch.commit();
  return ref;
}

// Find an account by username (stored lowercase). Null if there's none.
export async function findUserByUsername(name: string) {
  const handle = name.trim().replace(/^@/, "").toLowerCase();
  if (!handle) return null;
  const snap = await getDocs(query(collection(db, "users"), where("username", "==", handle)));
  return snap.empty ? null : { userId: snap.docs[0].id, username: handle };
}

export async function getUsername(uid: string): Promise<string> {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? snap.data().username || "unknown" : "unknown";
}

// Signed board posts from before usernames were required may say
// "anonymous" or "unknown"; show the poster's current username instead.
// (Truly anonymous posts have no uid, so they're never looked up.)
export async function withPosterNames<T extends { uid?: string; username?: string }>(ads: T[]): Promise<T[]> {
  const names = new Map<string, Promise<string>>();
  return Promise.all(
    ads.map(async (ad) => {
      if (!ad.uid || (ad.username && ad.username !== "anonymous" && ad.username !== "unknown")) return ad;
      if (!names.has(ad.uid)) names.set(ad.uid, getUsername(ad.uid).catch(() => "unknown"));
      const name = await names.get(ad.uid)!;
      return name !== "unknown" ? { ...ad, username: name } : ad;
    })
  );
}

// Older requests were saved without a status, so treat those as pending.
export function isPending(status?: string) {
  return !status || status === "pending";
}

export const MAX_NOTE = 500;

export type RequestResult =
  | "sent"
  | "already-connected"
  | "already-sent"
  | "self"
  | "unavailable"
  | "slow-down"; // too soon after the last one; SlowDownNotice explains

export const requestResultMessage: Record<RequestResult, string> = {
  sent: "Pen pal request sent!",
  "already-connected": "You're already pen pals!",
  "already-sent": "You've already sent them a request.",
  self: "That's you!",
  unavailable: "Sorry, you can't send a request to this person.",
  "slow-down": "",
};

// ---------- Pen pal links ----------

export const pairId = (a: string, b: string) => (a < b ? `${a}_${b}` : `${b}_${a}`);

export async function arePenPals(a: string, b: string): Promise<boolean> {
  try {
    return (await getDoc(doc(db, "penpals", pairId(a, b)))).exists();
  } catch {
    return false;
  }
}

// Accepting writes the request and the link together, so they can't disagree.
export async function acceptRequest(
  requestId: string,
  fromUid: string,
  myUid: string,
  viaAnonymousAd: boolean
) {
  const batch = writeBatch(db);
  batch.update(
    doc(db, "penpalRequests", requestId),
    viaAnonymousAd ? { status: "accepted", to: myUid } : { status: "accepted" }
  );
  batch.set(doc(db, "penpals", pairId(fromUid, myUid)), {
    members: [fromUid, myUid].sort(),
    requestId,
    createdAt: serverTimestamp(),
  });
  await batch.commit();
}

// Pen pals from before links existed: create any missing links.
export async function ensureMyPenPalLinks(uid: string) {
  const [received, sent] = await Promise.all([
    getDocs(query(collection(db, "penpalRequests"), where("to", "==", uid))),
    getDocs(query(collection(db, "penpalRequests"), where("from", "==", uid))),
  ]);
  for (const d of [...received.docs, ...sent.docs]) {
    const r = d.data();
    if (r.status !== "accepted" || !r.from || !r.to) continue;
    const ref = doc(db, "penpals", pairId(r.from, r.to));
    try {
      if (!(await getDoc(ref)).exists()) {
        await setDoc(ref, {
          members: [r.from, r.to].sort(),
          requestId: d.id,
          createdAt: serverTimestamp(),
        });
      }
    } catch (err) {
      console.warn("Couldn't restore pen pal link:", err);
    }
  }
}

export type PenPal = {
  id: string;
  username: string;
  displayName: string;
  pronouns: string;
  avatarConfig?: unknown;
  avatar?: string;
  avatarTone?: string;
};

// Everyone this user is linked with, for the address book and letter picker.
export async function getMyPenPals(uid: string): Promise<PenPal[]> {
  await ensureMyPenPalLinks(uid).catch(() => undefined);
  const links = await getDocs(
    query(collection(db, "penpals"), where("members", "array-contains", uid))
  );
  const ids = links.docs
    .map((d) => (d.data().members as string[]).find((m) => m !== uid))
    .filter((m): m is string => !!m);
  const people = await Promise.all(
    ids.map(async (id) => {
      const snap = await getDoc(doc(db, "users", id));
      const u = snap.exists() ? snap.data() : {};
      return {
        id,
        username: u.username || "penpal",
        displayName: (u.visibility?.name && u.name) || "",
        pronouns: (u.visibility?.pronouns && u.pronouns) || "",
        avatarConfig: u.avatarConfig,
        avatar: u.avatar,
        avatarTone: u.avatarTone,
      };
    })
  );
  return people.sort((a, b) =>
    (a.displayName || a.username).localeCompare(b.displayName || b.username)
  );
}

// ---------- Requests and kudos ----------

export async function sendPenPalRequest(
  fromUid: string,
  toUid: string,
  message = ""
): Promise<RequestResult> {
  if (fromUid === toUid) return "self";

  // Look for an existing request in either direction.
  const [outgoing, incoming] = await Promise.all([
    getDocs(
      query(
        collection(db, "penpalRequests"),
        where("from", "==", fromUid),
        where("to", "==", toUid)
      )
    ),
    getDocs(
      query(
        collection(db, "penpalRequests"),
        where("from", "==", toUid),
        where("to", "==", fromUid)
      )
    ),
  ]);
  const existing = [...outgoing.docs, ...incoming.docs].map((d) => d.data());
  if (existing.some((r) => r.status === "blocked")) return "unavailable";
  if (existing.some((r) => r.status === "accepted")) return "already-connected";
  if (outgoing.docs.some((d) => isPending(d.data().status))) return "already-sent";

  try {
    await pacedAdd(fromUid, "request", "penpalRequests", {
      from: fromUid,
      fromUsername: await getUsername(fromUid),
      to: toUid,
      message: message.trim().slice(0, MAX_NOTE),
      status: "pending",
      timestamp: serverTimestamp(),
    });
  } catch (err) {
    if (isSlowDown(err)) return "slow-down";
    // The database refuses requests to someone who blocked you.
    return "unavailable";
  }
  return "sent";
}

// Null means it was too soon after the last kudos (the note explains).
// `note` says what it was for, e.g. 'for your post "vibe check"'.
export async function sendKudos(fromUid: string, toUid: string, note = ""): Promise<boolean | null> {
  try {
    await pacedAdd(fromUid, "kudos", "kudos", {
      from: fromUid,
      fromUsername: await getUsername(fromUid),
      to: toUid,
      ...(note ? { note: note.slice(0, 150) } : {}),
      timestamp: serverTimestamp(),
    });
    return true;
  } catch (err) {
    return isSlowDown(err) ? null : false;
  }
}

// ---------- Anonymous ads ----------
// An anonymous ad stores no account ID. Ownership lives only in the
// poster's private doc (users/{uid}/private/anon), and kudos and requests
// are addressed to the ad itself, then delivered from there.

export const anonDocRef = (uid: string) => doc(db, "users", uid, "private", "anon");

export async function getMyAnonAdIds(uid: string): Promise<string[]> {
  const snap = await getDoc(anonDocRef(uid));
  return snap.exists() ? snap.data().adIds || [] : [];
}

// Mail addressed to anonymous ads is fetched one ad at a time, which is
// what the security rules can verify ("this ad is in my private list").
export function perAd<T>(adIds: string[], make: (adId: string) => T): T[] {
  return adIds.slice(0, 50).map(make);
}

export async function postAnonymousAd(uid: string, ad: Record<string, unknown>) {
  await checkPace(uid, "post");
  const adRef = doc(collection(db, "penpalAds"));
  // Record ownership first, so replies are never lost.
  await setDoc(anonDocRef(uid), { adIds: arrayUnion(adRef.id) }, { merge: true });
  const batch = writeBatch(db);
  markPace(batch, uid, "post");
  batch.set(adRef, { ...ad, username: "anonymous", anonymous: true });
  await batch.commit();
  return adRef.id;
}

export async function sendRequestToAd(
  fromUid: string,
  adId: string,
  message = ""
): Promise<RequestResult> {
  const existing = await getDocs(
    query(
      collection(db, "penpalRequests"),
      where("from", "==", fromUid),
      where("toAd", "==", adId)
    )
  );
  if (existing.docs.some((d) => d.data().status === "blocked")) return "unavailable";
  if (existing.docs.some((d) => d.data().status === "accepted")) {
    return "already-connected";
  }
  if (existing.docs.some((d) => isPending(d.data().status))) return "already-sent";

  try {
    await pacedAdd(fromUid, "request", "penpalRequests", {
      from: fromUid,
      fromUsername: await getUsername(fromUid),
      toAd: adId,
      message: message.trim().slice(0, MAX_NOTE),
      status: "pending",
      timestamp: serverTimestamp(),
    });
  } catch (err) {
    return isSlowDown(err) ? "slow-down" : "unavailable";
  }
  return "sent";
}

export async function sendKudosToAd(fromUid: string, adId: string): Promise<boolean | null> {
  try {
    await pacedAdd(fromUid, "kudos", "kudos", {
      from: fromUid,
      fromUsername: await getUsername(fromUid),
      toAd: adId,
      timestamp: serverTimestamp(),
    });
    return true;
  } catch (err) {
    return isSlowDown(err) ? null : false;
  }
}

export async function deleteMyAd(adId: string) {
  await deleteDoc(doc(db, "penpalAds", adId));
}

// ---------- Blocking ----------

export const blocksCollection = (uid: string) => collection(db, "users", uid, "blocks");

export async function blockUser(myUid: string, otherUid: string, username = "") {
  await setDoc(doc(db, "users", myUid, "blocks", otherUid), {
    username,
    blockedAt: serverTimestamp(),
  });

  // End the pen pal link, if there is one.
  const link = doc(db, "penpals", pairId(myUid, otherUid));
  try {
    if ((await getDoc(link)).exists()) await deleteDoc(link);
  } catch (err) {
    console.warn("Couldn't remove pen pal link:", err);
  }

  // Close any requests between us so the link isn't restored.
  const [mine, theirs] = await Promise.all([
    getDocs(
      query(
        collection(db, "penpalRequests"),
        where("from", "==", myUid),
        where("to", "==", otherUid)
      )
    ),
    getDocs(
      query(
        collection(db, "penpalRequests"),
        where("from", "==", otherUid),
        where("to", "==", myUid)
      )
    ),
  ]);
  await Promise.all(
    [...mine.docs, ...theirs.docs].map((d) =>
      updateDoc(d.ref, { status: "blocked" }).catch(() => undefined)
    )
  );
}

// A gentler goodbye than blocking: the pen pal link ends (no new letters
// either way), past letters stay, and either person can send a new request
// later. They aren't told. Accepted requests are marked "ended" so the link
// isn't restored by ensureMyPenPalLinks.
export async function removePenPal(myUid: string, otherUid: string) {
  const [mine, theirs] = await Promise.all([
    getDocs(
      query(collection(db, "penpalRequests"), where("from", "==", myUid), where("to", "==", otherUid))
    ),
    getDocs(
      query(collection(db, "penpalRequests"), where("from", "==", otherUid), where("to", "==", myUid))
    ),
  ]);
  await Promise.all(
    [...mine.docs, ...theirs.docs]
      .filter((d) => d.data().status === "accepted")
      .map((d) => updateDoc(d.ref, { status: "ended" }))
  );
  const link = doc(db, "penpals", pairId(myUid, otherUid));
  if ((await getDoc(link)).exists()) await deleteDoc(link);
}

export async function unblockUser(myUid: string, otherUid: string) {
  await deleteDoc(doc(db, "users", myUid, "blocks", otherUid));
}

// ---------- Reports ----------

export const REPORT_REASONS = [
  "Harassment or bullying",
  "Hate speech (transphobia, racism, ableism...)",
  "Sexual or explicit content",
  "Seems to be under 18",
  "Spam or scam",
  "Impersonation or fake identity",
  "Sharing private information",
  "Something else",
];

export type ReportTarget = {
  userId?: string;
  username?: string;
  adId?: string;
  messageId?: string;
  requestId?: string;
  threadId?: string; // a community room post (or, with circleId, a circle post)...
  replyId?: string; // ...or one of its comments
  circleId?: string; // set when the post is in a private circle
  pieceId?: string; // a PenPal Post piece (with replyId for one of its comments)
};

export async function submitReport(
  reporterUid: string,
  target: ReportTarget,
  reason: string,
  details: string
) {
  const evidence = await copyEvidence(target).catch(() => null);
  const report = {
    reporter: reporterUid,
    ...Object.fromEntries(Object.entries(target).filter(([, v]) => v)),
    reason,
    details: details.trim().slice(0, 2000),
    ...(evidence ? { evidence } : {}),
  };
  const ref = await pacedAdd(reporterUid, "report", "reports", {
    ...report,
    status: "open",
    createdAt: serverTimestamp(),
  });
  // The report is saved either way; the email is a heads-up for moderators.
  emailReport(ref.id, report, await getUsername(reporterUid).catch(() => "")).catch((err) =>
    console.error("Report email failed", err)
  );
}

export type Evidence = { kind: string; from?: string; subject?: string; body: string };

// A copy of what's being reported, as the reporter sees it, so moderators
// can still read it if it's edited or deleted later.
async function copyEvidence(target: ReportTarget): Promise<Evidence | null> {
  const clip = (v: unknown, max: number) => String(v ?? "").slice(0, max);
  if (target.messageId) {
    const m = await getDoc(doc(db, "messages", target.messageId));
    if (!m.exists()) return null;
    const d = m.data();
    return {
      kind: "letter",
      from: clip(d.senderUsername, 60),
      subject: clip(d.subject, 300),
      body: clip(d.body, 10000),
    };
  }
  if (target.pieceId) {
    const t = await getDoc(doc(db, "postPieces", target.pieceId));
    if (target.replyId) {
      const r = await getDoc(doc(db, "postPieces", target.pieceId, "replies", target.replyId));
      if (!r.exists()) return null;
      const d = r.data();
      return {
        kind: "post comment",
        from: clip(d.username, 60),
        subject: clip(t.exists() ? `Re: ${t.data().title}` : "", 300),
        body: clip(d.body, 10000),
      };
    }
    if (!t.exists()) return null;
    const d = t.data();
    return { kind: "post piece", from: clip(d.username, 60), subject: clip(d.title, 300), body: clip(d.body, 10000) };
  }
  if (target.threadId) {
    // a room post, or (with circleId) a post in a private circle
    const base = target.circleId
      ? ["circles", target.circleId, "posts", target.threadId]
      : ["roomThreads", target.threadId];
    const where = target.circleId ? "circle" : "room";
    const t = await getDoc(doc(db, base[0], ...base.slice(1)));
    if (target.replyId) {
      const r = await getDoc(doc(db, base[0], ...base.slice(1), "replies", target.replyId));
      if (!r.exists()) return null;
      const d = r.data();
      return {
        kind: `${where} reply`,
        from: clip(d.username, 60),
        subject: clip(t.exists() ? `Re: ${t.data().title}` : "", 300),
        body: clip(d.body, 10000),
      };
    }
    if (!t.exists()) return null;
    const d = t.data();
    return { kind: `${where} post`, from: clip(d.username, 60), subject: clip(d.title, 300), body: clip(d.body, 10000) };
  }
  if (target.requestId) {
    const r = await getDoc(doc(db, "penpalRequests", target.requestId));
    if (!r.exists()) return null;
    const d = r.data();
    return { kind: "request", from: clip(d.senderUsername, 60), body: clip(d.message, 10000) };
  }
  if (target.adId) {
    const a = await getDoc(doc(db, "penpalAds", target.adId));
    if (!a.exists()) return null;
    const d = a.data();
    return {
      kind: "post",
      from: clip(d.username || "anonymous", 60),
      subject: clip(d.subject, 300),
      body: clip(d.message, 10000),
    };
  }
  return null;
}

// Category names are stored with emoji; show them as plain text.
export function stripEmoji(text: string) {
  return text
    .replace(/[\p{Extended_Pictographic}\p{Emoji_Modifier}‍️⃣]/gu, "")
    .replace(/[\u{1F1E6}-\u{1F1FF}]/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}
